import { Box } from "@mui/material";
import WatchOutlined from "@mui/icons-material/WatchOutlined";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import CustomTooltip from "../../../../common/CustomTooltip";
import {
  eventCalories,
  eventDisplayName,
  formatClockTime,
  wearableSessions,
} from "../../helpers/activitySelectors";
import { MIN_HR_SAMPLES_FOR_ANALYSIS } from "../../model/workoutEnums";
import { ActivityEvent, WearableSession } from "../../model/workoutPlanTypes";
import { useWorkoutPlan } from "../../store/useWorkoutPlan";
import WorkoutTypeIcon from "../shared/WorkoutTypeIcon";

type Props = {
  /** Tracked entries (their merged `events` are expanded) or sessions themselves. */
  events: Array<ActivityEvent | WearableSession>;
};

/**
 * The mobile "Wearable Data" block: the heading, "N Workouts" when more than
 * one session was recorded, then one `WorkoutWearableDataWidget` per
 * session — the type glyph and name, the calories, and "HH:mm,  N min". The
 * sessions come from the entries' merged `events`, as on mobile. A session
 * with a heart-rate series opens its analysis when the member has advanced
 * app access (mobile: `hr_details` longer than five samples and
 * `has_advanced_app_access`); any other widget carries the platform tooltip
 * with the mobile's reason ("No HR data." when the member qualifies but the
 * session has none) and is not pressable.
 */
const WearableDataSection = ({ events }: Props) => {
  const theme: Theme = useTheme();
  const sessions = wearableSessions(events as ActivityEvent[]);
  if (sessions.length === 0) return null;

  return (
    <Box sx={styles.section}>
      <Box sx={styles.title(theme)}>Wearable Data</Box>
      {sessions.length > 1 && (
        <Box sx={styles.subtitle(theme)}>{sessions.length} Workouts</Box>
      )}
      <Box sx={styles.grid}>
        {sessions.map((session, index) => (
          <WearableDataWidget key={session.id ?? index} session={session} />
        ))}
      </Box>
    </Box>
  );
};

export default WearableDataSection;

export const WearableDataWidget = ({ session: event }: { session: WearableSession }) => {
  const theme: Theme = useTheme();
  const { openAnalysis, analysisEnabled } = useWorkoutPlan();

  const hasGlyph = event.type !== undefined && [1, 2, 3, 4].includes(event.type);
  const hasHr = (event.hr_details?.length ?? 0) >= MIN_HR_SAMPLES_FOR_ANALYSIS;
  const canAnalyse = analysisEnabled && !!event.id && hasHr;
  // Mobile: "No HR data." only when the member qualifies for the analysis
  // and the session simply has no series; otherwise the bare message.
  const reason =
    analysisEnabled && !hasHr ? "No HR data. Analysis not available." : "Analysis not available.";
  const open = () => {
    if (canAnalyse && event.id) openAnalysis({ eventId: event.id, title: eventDisplayName(event) });
  };

  const card = (
    <Box
      sx={styles.widget(theme, canAnalyse)}
      onClick={canAnalyse ? open : undefined}
      role={canAnalyse ? "button" : undefined}
      tabIndex={canAnalyse ? 0 : undefined}
      aria-label={canAnalyse ? eventDisplayName(event) : undefined}
      onKeyDown={
        canAnalyse
          ? (keyEvent) => {
              if (keyEvent.key === "Enter" || keyEvent.key === " ") {
                keyEvent.preventDefault();
                open();
              }
            }
          : undefined
      }
    >
      <Box sx={styles.widgetTitleRow}>
        <Box sx={styles.iconDisc(theme)}>
          {hasGlyph ? (
            <WorkoutTypeIcon type={event.type ?? 0} fill={theme.colors_ds.NEUTRAL_TEXT_STRONG} />
          ) : (
            <WatchOutlined sx={{ fontSize: 14, color: theme.colors_ds.NEUTRAL_TEXT_STRONG }} />
          )}
        </Box>
        <Box sx={styles.widgetTitle(theme)}>{eventDisplayName(event)}</Box>
      </Box>
      <Box sx={styles.calories(theme)}>{Math.round(eventCalories(event))} Kcal</Box>
      <Box sx={styles.time(theme)}>
        {formatClockTime(event.start) || "--:--"},&nbsp;&nbsp;
        {event.duration_in_minutes != null ? Math.round(event.duration_in_minutes) : "--"} min
      </Box>
    </Box>
  );

  if (canAnalyse) return card;
  return (
    // `customHeight="auto"`: the tooltip's anchor box defaults to a fixed
    // height meant for icons, which would let the cards overlap each other.
    <CustomTooltip message={reason} type="medium" customHeight="auto">
      {card}
    </CustomTooltip>
  );
};

const styles = {
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "18px",
    lineHeight: "24px",
  }),
  subtitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  // One session fills the width (the mobile widget is flex: 1); several share it.
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginTop: "8px",
  },
  widget: (theme: Theme, clickable: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "center",
    minHeight: "94px",
    padding: "8px 12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: clickable ? "pointer" : "default",
    transition: "border-color 0.2s",
    "&:hover": clickable ? { borderColor: theme.colorTokens.P[60] } : {},
    "&:focus-visible": {
      outline: `2px solid ${theme.colorTokens.P[60]}`,
      outlineOffset: "2px",
    },
  }),
  widgetTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  iconDisc: (theme: Theme) => ({
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    borderRadius: "14px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
  }),
  widgetTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  calories: (theme: Theme) => ({
    marginBottom: "4px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  time: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
};
